import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import digitalCardsService from "./digitalCardsService";

const initialState = {
  cards: [],
  listLoading: false,
  listError: false,
  listMessage: "",
  updateLoading: false,
  updateError: false,
  updateMessage: "",
  createLoading: false,
  createError: false,
  createMessage: "",
};

export const digitalCardsSlice = createSlice({
  name: "digitalCards",
  initialState,
  reducers: {
    reset: () => initialState,
    setCardActiveOptimistic: (state, action) => {
      const { id, isActive, updatedAt } = action.payload || {};
      const target = state.cards.find((card) => card.id === id);
      if (target) {
        target.isActive = isActive;
        if (updatedAt) target.updatedAt = updatedAt;
      }
    },
    revertCardActive: (state, action) => {
      const { id, prevIsActive } = action.payload || {};
      const target = state.cards.find((card) => card.id === id);
      if (target) {
        target.isActive = prevIsActive;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(listDigitalCards.pending, (state) => {
        state.listLoading = true;
        state.listError = false;
        state.listMessage = "";
      })
      .addCase(listDigitalCards.fulfilled, (state, action) => {
        state.listLoading = false;
        state.listError = false;
        state.cards = action.payload;
      })
      .addCase(listDigitalCards.rejected, (state, action) => {
        state.listLoading = false;
        state.listError = true;
        state.listMessage = action.payload;
      })

      .addCase(updateDigitalCardActive.pending, (state) => {
        state.updateLoading = true;
        state.updateError = false;
        state.updateMessage = "";
      })
      .addCase(updateDigitalCardActive.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateError = false;
        const updated = action.payload;
        const target = state.cards.find((card) => card.id === updated?.id);
        if (target) Object.assign(target, updated);
      })
      .addCase(updateDigitalCardActive.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = true;
        state.updateMessage = action.payload;
        const { id, prevIsActive } = action.meta?.arg || {};
        const target = state.cards.find((card) => card.id === id);
        if (target && prevIsActive !== undefined) target.isActive = prevIsActive;
      })

      .addCase(updateDigitalCard.pending, (state) => {
        state.updateLoading = true;
        state.updateError = false;
        state.updateMessage = "";
      })
      .addCase(updateDigitalCard.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateError = false;
        const updated = action.payload;
        const target = state.cards.find((card) => card.id === updated?.id);
        if (target) Object.assign(target, updated);
      })
      .addCase(updateDigitalCard.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = true;
        state.updateMessage = action.payload;
      })

      .addCase(deleteDigitalCard.pending, (state) => {
        state.updateLoading = true;
        state.updateError = false;
        state.updateMessage = "";
      })
      .addCase(deleteDigitalCard.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateError = false;
        const deletedId = action.payload?.id;
        if (deletedId) state.cards = state.cards.filter((c) => c.id !== deletedId);
      })
      .addCase(deleteDigitalCard.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = true;
        state.updateMessage = action.payload;
      })

      .addCase(createDigitalCard.pending, (state) => {
        state.createLoading = true;
        state.createError = false;
        state.createMessage = "";
      })
      .addCase(createDigitalCard.fulfilled, (state) => {
        state.createLoading = false;
        state.createError = false;
      })
      .addCase(createDigitalCard.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = true;
        state.createMessage = action.payload;
      });
  },
});

export const listDigitalCards = createAsyncThunk("digitalCards/list", async (_, thunkAPI) => {
  try {
    return await digitalCardsService.listDigitalCards();
  } catch (error) {
    const message = error?.message || "Kartvizitler yüklenemedi.";
    return thunkAPI.rejectWithValue(message);
  }
});

export const createDigitalCard = createAsyncThunk("digitalCards/create", async (payload, thunkAPI) => {
  try {
    return await digitalCardsService.createDigitalCard(payload);
  } catch (error) {
    const message = error?.message || "Kartvizit oluşturulamadı.";
    return thunkAPI.rejectWithValue(message);
  }
});

export const updateDigitalCard = createAsyncThunk("digitalCards/update", async ({ id, data }, thunkAPI) => {
  try {
    return await digitalCardsService.updateDigitalCard(id, data);
  } catch (error) {
    const message = error?.message || "Kartvizit güncellenemedi.";
    return thunkAPI.rejectWithValue(message);
  }
});

export const updateDigitalCardActive = createAsyncThunk(
  "digitalCards/updateActive",
  async ({ id, isActive }, thunkAPI) => {
    try {
      return await digitalCardsService.updateDigitalCardActive(id, isActive);
    } catch (error) {
      const message = error?.message || "Durum güncellenemedi.";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const deleteDigitalCard = createAsyncThunk("digitalCards/delete", async ({ id }, thunkAPI) => {
  try {
    return await digitalCardsService.deleteDigitalCard(id);
  } catch (error) {
    const message = error?.message || "Kartvizit silinemedi.";
    return thunkAPI.rejectWithValue(message);
  }
});

export const { reset, setCardActiveOptimistic, revertCardActive } = digitalCardsSlice.actions;
export const selectDigitalCards = (state) => state.digitalCards.cards;
export const selectDigitalCardsLoading = (state) => state.digitalCards.listLoading;
export const selectDigitalCardsError = (state) => state.digitalCards.listError;
export const selectDigitalCardsMessage = (state) => state.digitalCards.listMessage;
export const selectDigitalCardsUpdateError = (state) => state.digitalCards.updateError;
export const selectDigitalCardsUpdateMessage = (state) => state.digitalCards.updateMessage;
export const selectDigitalCardsCreateLoading = (state) => state.digitalCards.createLoading;
export const selectDigitalCardsCreateError = (state) => state.digitalCards.createError;
export const selectDigitalCardsCreateMessage = (state) => state.digitalCards.createMessage;

export default digitalCardsSlice.reducer;
